import { notification } from "antd";
import { makeAutoObservable, runInAction } from "mobx";
import { chatService } from "@/services/ChatService";
import { ChatMapper, ChannelType } from "@/types/chat";
import { onError } from "@/utils/onError";
import { getActiveRouteInfo } from "@/utils/orderRoute";
import { playNotificationSound, registerChatServiceWorker, requestNotificationPermission, showLocalNotification, } from "@/utils/chatNotifications";
const POSTS_PER_PAGE = 60;
export class ChatStore {
    /** Ответ относится к текущему соединению (после `disconnect()` — уже нет). */
    isCurrent(generation) {
        return generation === this.connectionGeneration;
    }
    constructor() {
        // Panel state
        this.isPanelOpen = false;
        this.activeChannelId = null;
        this.activeThreadRootId = null;
        // Data
        this.channels = new Map();
        this.messages = new Map();
        this.threadMessages = new Map();
        // Loading
        this.isChannelsLoading = false;
        this.isMessagesLoading = false;
        this.isSending = false;
        // Tracks channels where all history has been loaded (no older messages)
        this.channelsFullyLoaded = new Set();
        // Connection
        this.isConnected = false;
        this.mmToken = null;
        this.currentUserId = null;
        this.botUserId = null;
        this.teamId = null;
        // Error
        this.error = null;
        /**
         * WP-DSP-FIX-01: момент последнего сообщения, достойного внимания (не своё, не в открытом канале) —
         * кнопка «Чат» в шапке кратко подсвечивается. 0 — подсветки не было.
         */
        this.attentionAt = 0;
        // Pending order thread (for creating new thread)
        this.pendingOrderThread = null;
        // Internal
        this.wsHandler = null;
        this.swRegistration = null;
        this.notificationPermissionRequested = false;
        this.initPromise = null;
        /**
         * WP-DSP-FIX-01: номер соединения. `disconnect()` его увеличивает, и подключение, начатое до выхода,
         * не дописывает состояние и не открывает сокет после него (выход во время подключения, двойной эффект StrictMode).
         */
        this.connectionGeneration = 0;
        this.swListenerAttached = false;
        this.isInitializing = false;
        this.lastReconnectAt = 0;
        makeAutoObservable(this, {}, { autoBind: true });
    }
    // --- Computed ---
    get totalUnreadCount() {
        let count = 0;
        for (const channel of this.channels.values()) {
            count += channel.unreadCount;
        }
        return count;
    }
    get routeChannels() {
        return Array.from(this.channels.values())
            .filter((ch) => ch.type === ChannelType.ROUTE)
            .sort((a, b) => b.lastPostAt - a.lastPostAt);
    }
    get supportChannels() {
        return Array.from(this.channels.values())
            .filter((ch) => ch.type === ChannelType.SUPPORT)
            .sort((a, b) => b.lastPostAt - a.lastPostAt);
    }
    get systemChannels() {
        return Array.from(this.channels.values())
            .filter((ch) => ch.type === ChannelType.SYSTEM)
            .sort((a, b) => b.lastPostAt - a.lastPostAt);
    }
    get activeChannel() {
        if (!this.activeChannelId)
            return null;
        return this.channels.get(this.activeChannelId) ?? null;
    }
    get activeMessages() {
        if (this.activeThreadRootId) {
            return this.threadMessages.get(this.activeThreadRootId) ?? [];
        }
        if (this.activeChannelId) {
            return this.messages.get(this.activeChannelId) ?? [];
        }
        return [];
    }
    // --- Lifecycle ---
    retryInit() {
        this.error = null;
        // Подключение уже идёт — второе не запускаем (иначе второй сокет и двойная обработка постов)
        if (this.isInitializing)
            return;
        this.initPromise = null;
        this.initChat();
    }
    /**
     * Подключение к чату (идемпотентно). WP-DSP-FIX-01: вызывается сразу после входа персонала (`AppLayout`) с
     * `silent: true` — ошибка не всплывает уведомлением, а остаётся в `error` (панель показывает её с «Повторить»).
     */
    async initChat(options = {}) {
        if (this.isConnected)
            return;
        if (!this.initPromise) {
            this.initPromise = this.performInit(options.silent ?? false);
        }
        return this.initPromise;
    }
    async performInit(silent) {
        const generation = this.connectionGeneration;
        const isStale = () => !this.isCurrent(generation);
        this.isInitializing = true;
        try {
            const token = await chatService.fetchMmToken();
            if (isStale())
                return;
            const mmUrl = process.env.REACT_APP_MATTERMOST_URL || "";
            chatService.init(token, mmUrl);
            const me = await chatService.getMe();
            if (isStale())
                return;
            // Resolve team ID by name
            const teamName = process.env.REACT_APP_MATTERMOST_TEAM_NAME || "evcharge";
            const { Client4 } = await import("@mattermost/client");
            const tempClient = new Client4();
            tempClient.setUrl(mmUrl);
            tempClient.setToken(token);
            const team = await tempClient.getTeamByName(teamName);
            if (isStale())
                return;
            // Resolve bot user ID for author type detection (bot vs client messages)
            let resolvedBotUserId = null;
            try {
                const botUsername = process.env.REACT_APP_MATTERMOST_BOT_USERNAME || "evcharge-bot";
                const botUser = await tempClient.getUserByUsername(botUsername);
                resolvedBotUserId = botUser.id;
            }
            catch {
                // Bot user may not exist yet — graceful degradation
            }
            if (isStale())
                return;
            runInAction(() => {
                this.mmToken = token;
                this.currentUserId = me.id;
                this.teamId = team.id;
                this.botUserId = resolvedBotUserId;
                this.isConnected = true;
                this.error = null;
            });
            if (this.wsHandler) {
                chatService.offMessage(this.wsHandler);
                this.wsHandler = null;
            }
            chatService.connectWebSocket();
            this.setupWebSocketHandler();
            this.setupReconnectHandler(generation);
            await this.loadChannels();
            // UC1005: Register service worker for push notifications
            try {
                const registration = await registerChatServiceWorker();
                runInAction(() => {
                    this.swRegistration = registration;
                });
            }
            catch {
                console.error("Failed to register chat service worker");
            }
            // UC1005: Listen for SW messages (notification clicks)
            this.setupServiceWorkerListener();
        }
        catch (error) {
            if (isStale())
                return;
            if (silent) {
                console.error("Ошибка инициализации чата", error);
            }
            else {
                onError(error, "Ошибка инициализации чата");
            }
            runInAction(() => {
                this.error = error instanceof Error ? error.message : "Ошибка инициализации чата";
                this.initPromise = null;
            });
        }
        finally {
            if (this.isCurrent(generation)) {
                runInAction(() => {
                    this.isInitializing = false;
                });
            }
        }
    }
    async loadChannels() {
        if (!this.teamId)
            return;
        const generation = this.connectionGeneration;
        // Show loading skeleton only on initial load (no channels yet)
        const isInitialLoad = this.channels.size === 0;
        if (isInitialLoad) {
            this.isChannelsLoading = true;
        }
        try {
            const [serverChannels, memberships] = await Promise.all([
                chatService.getMyChannels(this.teamId),
                chatService.getMyChannelMembers(this.teamId),
            ]);
            const membershipMap = new Map(memberships.map((m) => [m.channel_id, m]));
            runInAction(() => {
                // Пока шёл запрос, могли выйти — каналы прежней сессии в стор не пишем
                if (!this.isCurrent(generation))
                    return;
                this.channels.clear();
                for (const sc of serverChannels) {
                    const membership = membershipMap.get(sc.id);
                    const unreadCount = membership
                        ? sc.total_msg_count - membership.msg_count
                        : 0;
                    const channel = ChatMapper.channelToDomain(sc, Math.max(0, unreadCount));
                    this.channels.set(channel.id, channel);
                }
            });
            // Fetch last message preview for each channel (non-blocking)
            if (this.isCurrent(generation))
                this.loadChannelPreviews(serverChannels.map((sc) => sc.id));
        }
        catch (error) {
            onError(error, "Ошибка загрузки каналов");
        }
        finally {
            runInAction(() => {
                this.isChannelsLoading = false;
            });
        }
    }
    loadChannelPreviews(channelIds) {
        const generation = this.connectionGeneration;
        for (const channelId of channelIds) {
            chatService
                .getPosts(channelId, 0, 1)
                .then((postList) => {
                const postId = postList.order[0];
                const post = postId ? postList.posts[postId] : null;
                if (!post)
                    return;
                const preview = post.message.slice(0, 50);
                runInAction(() => {
                    if (!this.isCurrent(generation))
                        return;
                    const channel = this.channels.get(channelId);
                    if (channel) {
                        this.channels.set(channelId, { ...channel, lastMessagePreview: preview });
                    }
                });
            })
                .catch(() => {
                // Non-critical — preview stays empty
            });
        }
    }
    /** Закрыть соединение и очистить данные сессии чата. WP-DSP-FIX-01: вызывается при выходе персонала. */
    disconnect() {
        this.connectionGeneration += 1;
        this.isInitializing = false;
        if (this.wsHandler) {
            chatService.offMessage(this.wsHandler);
            this.wsHandler = null;
        }
        chatService.disconnect();
        this.initPromise = null;
        runInAction(() => {
            this.isConnected = false;
            this.isPanelOpen = false;
            this.isChannelsLoading = false;
            this.error = null;
            this.attentionAt = 0;
            this.mmToken = null;
            this.currentUserId = null;
            this.botUserId = null;
            this.teamId = null;
            this.channels.clear();
            this.messages.clear();
            this.threadMessages.clear();
            this.activeChannelId = null;
            this.activeThreadRootId = null;
            this.pendingOrderThread = null;
        });
    }
    // --- Panel ---
    openPanel() {
        this.isPanelOpen = true;
        // UC1005: Request notification permission on first panel open
        if (!this.notificationPermissionRequested) {
            this.notificationPermissionRequested = true;
            requestNotificationPermission().catch(() => { });
        }
    }
    closePanel() {
        this.isPanelOpen = false;
    }
    async openPanelWithRoute(routeId) {
        this.isPanelOpen = true;
        await this.initChat();
        // Derive shortId from routeId (first 4 chars of UUID)
        const shortId = routeId.slice(0, 4);
        // Search in loaded route channels for a match by shortId suffix
        const matched = this.routeChannels.find((ch) => ch.name.endsWith(`-${shortId}`));
        if (matched) {
            await this.selectChannel(matched.id);
        }
        else {
            notification.info({
                message: "Канал маршрута не найден",
                description: "Канал чата для этого маршрута ещё не создан",
            });
        }
    }
    async openPanelWithOrder(orderId) {
        // Lazy import to avoid circular dependency
        const { getRootStore } = await import("./RootStore");
        const { singleOrderStore } = getRootStore();
        const order = singleOrderStore.order;
        // WP-DSP-FIX-03: маршрут в статусе CANCELLED — заказ «без маршрута»
        const routeInfo = getActiveRouteInfo(order?.routeInfo);
        if (!order || !routeInfo) {
            notification.warning({
                message: "Заказ не назначен на маршрут",
                description: "Чат доступен только для заказов, включённых в маршрут",
            });
            return;
        }
        this.isPanelOpen = true;
        await this.initChat();
        const routeId = routeInfo.routeId;
        const orderNumber = order.orderNumber;
        // Open route channel
        const shortId = routeId.slice(0, 4);
        const matched = this.routeChannels.find((ch) => ch.name.endsWith(`-${shortId}`));
        if (!matched) {
            notification.info({
                message: "Канал маршрута не найден",
                description: "Канал чата для этого маршрута ещё не создан",
            });
            return;
        }
        await this.selectChannel(matched.id);
        // Search channel messages for a root post with orderId in props or order number in text
        const channelMsgs = this.messages.get(matched.id) ?? [];
        const threadRoot = channelMsgs.find((msg) => {
            if (msg.rootId)
                return false; // Skip replies
            const props = msg.props;
            if (props?.orderId === orderId)
                return true;
            if (msg.message.includes(`PZ-${orderNumber}`))
                return true;
            return false;
        });
        if (threadRoot) {
            await this.openThread(threadRoot.id);
        }
        else {
            // Show empty thread state — store context for creating thread later
            runInAction(() => {
                this.pendingOrderThread = { orderId, orderNumber, channelId: matched.id };
            });
        }
    }
    /** Create a new order discussion thread in the current route channel */
    async createOrderThread() {
        const pending = this.pendingOrderThread;
        if (!pending)
            return;
        this.isSending = true;
        try {
            const rootPost = await chatService.createPost(pending.channelId, `Обсуждение заказа PZ-${pending.orderNumber}`, undefined, undefined, { orderId: pending.orderId });
            runInAction(() => {
                this.pendingOrderThread = null;
            });
            await this.openThread(rootPost.id);
        }
        catch (error) {
            onError(error, "Ошибка создания обсуждения заказа");
        }
        finally {
            runInAction(() => {
                this.isSending = false;
            });
        }
    }
    async selectChannel(channelId) {
        this.activeChannelId = channelId;
        this.activeThreadRootId = null;
        this.pendingOrderThread = null;
        await this.loadMessages(channelId);
        this.markChannelRead(channelId);
    }
    async openThread(rootPostId) {
        const generation = this.connectionGeneration;
        this.activeThreadRootId = rootPostId;
        this.isMessagesLoading = true;
        try {
            const postList = await chatService.getPostThread(rootPostId);
            const msgs = await this.mapPostListToMessages(postList);
            runInAction(() => {
                if (!this.isCurrent(generation))
                    return;
                this.threadMessages.set(rootPostId, msgs);
            });
        }
        catch (error) {
            onError(error, "Ошибка загрузки треда");
        }
        finally {
            runInAction(() => {
                this.isMessagesLoading = false;
            });
        }
    }
    // --- Messages ---
    async loadMessages(channelId) {
        const generation = this.connectionGeneration;
        this.isMessagesLoading = true;
        this.channelsFullyLoaded.delete(channelId);
        try {
            const postList = await chatService.getPosts(channelId, 0, POSTS_PER_PAGE);
            const msgs = await this.mapPostListToMessages(postList);
            runInAction(() => {
                if (!this.isCurrent(generation))
                    return;
                this.messages.set(channelId, msgs);
            });
        }
        catch (error) {
            onError(error, "Ошибка загрузки сообщений");
        }
        finally {
            runInAction(() => {
                this.isMessagesLoading = false;
            });
        }
    }
    async loadOlderMessages(channelId) {
        if (this.channelsFullyLoaded.has(channelId))
            return;
        const existing = this.messages.get(channelId);
        if (!existing || existing.length === 0)
            return;
        const oldestMessage = existing[0];
        const generation = this.connectionGeneration;
        this.isMessagesLoading = true;
        try {
            const postList = await chatService.getPostsBefore(channelId, oldestMessage.id, POSTS_PER_PAGE);
            const olderMsgs = await this.mapPostListToMessages(postList);
            runInAction(() => {
                if (!this.isCurrent(generation))
                    return;
                if (olderMsgs.length === 0) {
                    this.channelsFullyLoaded.add(channelId);
                    return;
                }
                const current = this.messages.get(channelId) ?? [];
                // Защита от дублей по id (WP-DSP-3-FIX): пост, уже присутствующий в канале, не добавляется повторно
                const known = new Set(current.map((m) => m.id));
                this.messages.set(channelId, [...olderMsgs.filter((m) => !known.has(m.id)), ...current]);
            });
        }
        catch (error) {
            onError(error, "Ошибка загрузки старых сообщений");
        }
        finally {
            runInAction(() => {
                this.isMessagesLoading = false;
            });
        }
    }
    async sendMessage(text, fileIds) {
        const channelId = this.activeChannelId;
        if (!channelId)
            return;
        this.isSending = true;
        try {
            const rootId = this.activeThreadRootId ?? undefined;
            await chatService.createPost(channelId, text, rootId, fileIds);
        }
        catch (error) {
            onError(error, "Ошибка отправки сообщения");
        }
        finally {
            runInAction(() => {
                this.isSending = false;
            });
        }
    }
    async uploadFile(file) {
        const channelId = this.activeChannelId;
        if (!channelId)
            return [];
        try {
            const result = await chatService.uploadFile(channelId, file);
            return result.file_infos.map((fi) => fi.id);
        }
        catch (error) {
            onError(error, "Ошибка загрузки файла");
            return [];
        }
    }
    // --- WebSocket handlers ---
    handleNewPost(post) {
        const generation = this.connectionGeneration;
        const channelId = post.channel_id;
        const currentUserId = this.currentUserId ?? "";
        const botUserId = this.botUserId ?? "";
        const authorType = ChatMapper.getAuthorType(post, currentUserId, botUserId);
        const isOwnMessage = post.user_id === currentUserId;
        // Resolve author name from cache or use fallback
        const cachedUser = chatService.getUser(post.user_id);
        cachedUser
            .then((user) => {
            const msg = ChatMapper.postToDomain(post, user.username, authorType);
            runInAction(() => {
                // Пост прежней сессии после выхода не учитываем (ни счётчик, ни звук)
                if (!this.isCurrent(generation))
                    return;
                // Add to channel messages (повтор WS-кадра с тем же id — пропускаем, WP-DSP-3-FIX)
                const channelMsgs = this.messages.get(channelId);
                if (channelMsgs?.some((m) => m.id === msg.id))
                    return;
                if (channelMsgs) {
                    this.messages.set(channelId, [...channelMsgs, msg]);
                }
                // Add to thread messages if relevant
                if (post.root_id) {
                    const threadMsgs = this.threadMessages.get(post.root_id);
                    if (threadMsgs && !threadMsgs.some((m) => m.id === msg.id)) {
                        this.threadMessages.set(post.root_id, [...threadMsgs, msg]);
                    }
                    // Increment replyCount on the root post for ThreadPreview
                    if (channelMsgs) {
                        this.messages.set(channelId, (this.messages.get(channelId) ?? []).map((m) => m.id === post.root_id
                            ? { ...m, replyCount: m.replyCount + 1 }
                            : m));
                    }
                }
                // Update channel's lastPostAt, unread count, and preview
                const channel = this.channels.get(channelId);
                const isActiveChannel = this.activeChannelId === channelId && this.isPanelOpen;
                if (channel) {
                    this.channels.set(channelId, {
                        ...channel,
                        lastPostAt: post.create_at,
                        totalMsgCount: channel.totalMsgCount + 1,
                        unreadCount: isActiveChannel ? channel.unreadCount : channel.unreadCount + 1,
                        lastMessagePreview: msg.message.slice(0, 50),
                    });
                }
                // UC1005: Notifications
                if (!isOwnMessage && !isActiveChannel) {
                    this.attentionAt = Date.now();
                    const channelName = channel?.displayName ?? "Новое сообщение";
                    const preview = msg.message.slice(0, 80);
                    if (document.hidden && this.swRegistration) {
                        // Tab hidden → browser push + sound
                        showLocalNotification(channelName, preview, channelId, this.swRegistration);
                        playNotificationSound();
                    }
                    else {
                        // Tab active but panel closed or different channel → sound only
                        playNotificationSound();
                    }
                }
            });
        })
            .catch(() => {
            // Fallback with unknown author name
            const msg = ChatMapper.postToDomain(post, "unknown", authorType);
            runInAction(() => {
                if (!this.isCurrent(generation))
                    return;
                const channelMsgs = this.messages.get(channelId);
                if (channelMsgs && !channelMsgs.some((m) => m.id === msg.id)) {
                    this.messages.set(channelId, [...channelMsgs, msg]);
                }
            });
        });
    }
    handlePostEdited(post) {
        const channelId = post.channel_id;
        const updateInList = (msgs) => msgs.map((m) => m.id === post.id ? { ...m, message: post.message, fileIds: post.file_ids || [] } : m);
        const channelMsgs = this.messages.get(channelId);
        if (channelMsgs) {
            this.messages.set(channelId, updateInList(channelMsgs));
        }
        if (post.root_id) {
            const threadMsgs = this.threadMessages.get(post.root_id);
            if (threadMsgs) {
                this.threadMessages.set(post.root_id, updateInList(threadMsgs));
            }
        }
    }
    // --- Utils ---
    markChannelRead(channelId) {
        const channel = this.channels.get(channelId);
        if (channel && channel.unreadCount > 0) {
            this.channels.set(channelId, { ...channel, unreadCount: 0 });
        }
        // Fire and forget — mark read on server
        chatService.viewChannel(channelId).catch((error) => {
            console.error("Failed to mark channel as read:", error);
        });
    }
    // --- Private helpers ---
    setupWebSocketHandler() {
        this.wsHandler = (msg) => {
            if (msg.event === "posted") {
                const data = msg.data;
                try {
                    const post = JSON.parse(data.post);
                    this.handleNewPost(post);
                }
                catch {
                    console.error("Failed to parse posted message");
                }
            }
            else if (msg.event === "post_edited") {
                const data = msg.data;
                try {
                    const post = JSON.parse(data.post);
                    this.handlePostEdited(post);
                }
                catch {
                    console.error("Failed to parse edited message");
                }
            }
        };
        chatService.onMessage(this.wsHandler);
    }
    setupReconnectHandler(generation) {
        // Обработчики привязаны к своему соединению: сокет прежней сессии не меняет состояние новой (WP-DSP-FIX-01)
        chatService.onClose(() => {
            if (!this.isCurrent(generation))
                return;
            runInAction(() => {
                this.isConnected = false;
            });
        });
        chatService.onReconnect(() => {
            if (!this.isCurrent(generation))
                return;
            const now = Date.now();
            if (now - this.lastReconnectAt < ChatStore.RECONNECT_DEBOUNCE_MS)
                return;
            this.lastReconnectAt = now;
            runInAction(() => {
                this.isConnected = true;
            });
            // Reload channels to sync any missed messages (silent — no loading skeleton)
            this.loadChannels();
        });
    }
    /** UC1005: Listen for messages from service worker (notification click) */
    setupServiceWorkerListener() {
        // Слушатель вешается один раз за жизнь вкладки: после выхода и повторного входа не дублируется
        if (!("serviceWorker" in navigator) || this.swListenerAttached)
            return;
        this.swListenerAttached = true;
        navigator.serviceWorker.addEventListener("message", (event) => {
            const data = event.data;
            if (data?.type === "OPEN_CHAT" && data.channelId) {
                this.openPanel();
                this.selectChannel(data.channelId);
            }
        });
    }
    async mapPostListToMessages(postList) {
        const currentUserId = this.currentUserId ?? "";
        const botUserId = this.botUserId ?? "";
        // Prefetch all unique user IDs
        const userIds = new Set();
        for (const postId of postList.order) {
            const post = postList.posts[postId];
            if (post)
                userIds.add(post.user_id);
        }
        const userMap = new Map();
        await Promise.all(Array.from(userIds).map(async (uid) => {
            try {
                const user = await chatService.getUser(uid);
                userMap.set(uid, user);
            }
            catch {
                // User fetch failed — will use fallback name
            }
        }));
        // Map posts in chronological order (order is newest first)
        const reversedOrder = [...postList.order].reverse();
        const msgs = [];
        for (const postId of reversedOrder) {
            const post = postList.posts[postId];
            if (!post)
                continue;
            const authorType = ChatMapper.getAuthorType(post, currentUserId, botUserId);
            const user = userMap.get(post.user_id);
            const authorName = user?.username ?? "unknown";
            msgs.push(ChatMapper.postToDomain(post, authorName, authorType));
        }
        return msgs;
    }
}
ChatStore.RECONNECT_DEBOUNCE_MS = 5000;
